import { cn } from '@my/ui';
import type { RosterDriver } from '../lib/mockData';

const CAP: Record<string, string> = {
  GDP: 'bg-emerald-100 text-emerald-700',
  HAZ: 'bg-amber-100 text-amber-700',
  ADR: 'bg-red-100 text-red-700',
  TEMP: 'bg-sky-100 text-sky-700',
  FOOD: 'bg-lime-100 text-lime-700',
};

type DriveTone = 'ok' | 'tight' | 'risk' | 'unknown';

interface DriveState {
  tone: DriveTone;
  /** Right-aligned label next to "Drive time" (e.g. "3.8h left" / "unknown"). */
  leftLabel: string;
  /** Proportion of the bar filled (0–100). */
  pct: number;
  /** Small caption under the bar. */
  caption: string;
  /** Colour-coded status line. */
  status: string;
}

/** Full shift used to scale the remaining-drive bar. */
const SHIFT_HOURS = 4.5;

function driveState(d: RosterDriver): DriveState {
  if (d.driveRemainingH === null) {
    return {
      tone: 'unknown',
      leftLabel: 'unknown',
      pct: 100,
      caption: 'No tacho data — enter shift start',
      status: `Est. from plan — ${d.estFromPlanH ?? d.tachoMax}h`,
    };
  }
  const r = d.driveRemainingH;
  const pct = Math.max(6, Math.min(100, (r / SHIFT_HOURS) * 100));
  if (r > 2) return { tone: 'ok', leftLabel: `${r}h left`, pct, caption: 'Est. from plan', status: `On plan — ${r}h remaining` };
  if (r >= 0.5) return { tone: 'tight', leftLabel: `${r}h left`, pct, caption: 'Est. from plan', status: `Tight — ${r}h tacho remaining` };
  return { tone: 'risk', leftLabel: `${r}h left`, pct, caption: 'Est. from plan', status: `At risk — ${Math.round(r * 60)}min remaining` };
}

const BAR_FILL: Record<DriveTone, string> = {
  ok: 'bg-emerald-500',
  tight: 'bg-amber-400',
  risk: 'bg-red-500',
  unknown: 'bg-slate-300',
};

const STATUS_TEXT: Record<DriveTone, string> = {
  ok: 'text-emerald-600',
  tight: 'text-amber-600',
  risk: 'text-red-600',
  unknown: 'text-sky-600',
};

/**
 * Driver roster strip — always visible at the bottom. Light cockpit cards: name +
 * "start · depot · reg" subtitle, capability pills, a remaining drive-time bar
 * with a colour-coded status line, on-time %, and a "View day" button. In assign
 * mode (an unassigned leg is selected) cards highlight and the button becomes
 * "Assign →". Prototype — mock data.
 */
export function LegRoster({
  drivers,
  assignMode,
  onAssign,
  onOpenDay,
}: {
  drivers: RosterDriver[];
  assignMode: boolean;
  onAssign: (driverId: string) => void;
  onOpenDay: (driverId: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className="bg-slate-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white">
        Driver Roster
      </div>
      <div className="flex gap-2 overflow-x-auto bg-slate-50 p-2">
        {drivers.map((d) => (
          <Card key={d.id} d={d} assignMode={assignMode} onAssign={() => onAssign(d.id)} onOpenDay={() => onOpenDay(d.id)} />
        ))}
      </div>
    </div>
  );
}

function Card({
  d,
  assignMode,
  onAssign,
  onOpenDay,
}: {
  d: RosterDriver;
  assignMode: boolean;
  onAssign: () => void;
  onOpenDay: () => void;
}) {
  const drive = driveState(d);

  return (
    <div
      className={cn(
        'flex w-56 shrink-0 flex-col gap-2 rounded-md border bg-white p-3 text-xs shadow-sm transition-colors',
        assignMode ? 'border-emerald-400 ring-1 ring-emerald-400/40' : 'border-slate-200',
      )}
    >
      <div className="leading-tight">
        <div className="font-semibold text-slate-800">{d.name}</div>
        <div className="text-[11px] text-slate-500">
          {d.startLabel} · {d.reg}
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {d.capabilities.map((c) => (
          <span key={c} className={cn('rounded px-1.5 text-[9px] font-medium', CAP[c] ?? 'bg-slate-200 text-slate-700')}>
            {c}
          </span>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between text-[10px] font-medium text-slate-500">
          <span>Drive time</span>
          <span>{drive.leftLabel}</span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
          {drive.tone === 'unknown' ? (
            <div className="h-full w-full bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_4px,transparent_4px,transparent_8px)]" />
          ) : (
            <div className={cn('h-full rounded-full', BAR_FILL[drive.tone])} style={{ width: `${drive.pct}%` }} />
          )}
        </div>
        <div className="mt-1 text-[10px] text-slate-400">{drive.caption}</div>
        <div className={cn('text-[11px] font-medium', STATUS_TEXT[drive.tone])}>{drive.status}</div>
      </div>

      <div className="text-[10px] text-slate-500">On-time: {d.onTimePct}%</div>

      <button
        type="button"
        onClick={assignMode ? onAssign : onOpenDay}
        className={cn(
          'mt-auto rounded-md px-2 py-1.5 text-center text-[11px] font-medium transition-colors',
          assignMode
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
        )}
      >
        {assignMode ? 'Assign →' : 'View day'}
      </button>
    </div>
  );
}
